/**
 * Notification Bell Component
 *
 * Displays notification count and provides access to notifications.
 *
 * KAN-592. This used to be a DaisyUI CSS-only dropdown: `class="dropdown
 * dropdown-end"` on the wrapper, `tabindex="0"` on both the trigger and the
 * panel, and `w-80` on the panel. Two faults compounded and pushed the panel off
 * the LEFT edge of the screen at every phone width in the report (320-430px):
 *
 *   1. `w-80` is a hard 20rem = 320px with no viewport relation. At a 320px
 *      screen the panel alone is the entire viewport, margins included.
 *   2. `dropdown-end` right-anchors the panel to the TRIGGER's right edge, and
 *      the bell is NOT at the right edge of the header. In
 *      `main-layout.component.html` it sits before `<app-help-button>`, and
 *      `<app-organization-switcher>` carries `order-last grow basis-44` - so
 *      the switcher wraps to its own row at narrow widths and sits BESIDE the
 *      icons at wider ones, pushing the bell FURTHER left as the viewport gets
 *      WIDER. That is why the reporter measured 430px as worse than 320px.
 *
 * So the panel's left edge landed at `bellRight - 320`. Left overflow creates no
 * horizontal page scroll, which is why `e2e/tests/app-shell-horizontal-overflow.spec.ts`
 * never caught it: the page was genuinely not scrolling sideways, the content was
 * simply unreachable past the left edge.
 *
 * WHY A WIDTH CAP ALONE IS NOT THE FIX. At 430px with the bell at x=250, a
 * 320px-capped panel right-anchored to the bell still starts at -70. The LEFT
 * EDGE has to be clamped to the viewport. That is arithmetic that needs the
 * bell's real position, so it needs JS - `computeNotificationPanelPlacement`
 * in `notification-panel-position.ts`, which is pure and unit-tested at every
 * reported width across every bell position in the row.
 *
 * WHY THE DROPDOWN BECAME ANGULAR-MANAGED. The positioning needs to measure on
 * open, so JS was required regardless; and the DaisyUI CSS-only
 * `tabindex="0"` + `:focus-within` pattern is a known defect in this repo - the
 * blur fires before the click lands, so menu items need two clicks. Replacing it
 * with an explicit boolean + `(click)` + `@if` + document-level outside-click
 * removes that whole class of bug. The trigger is now a real
 * `<button type="button">`, which it should always have been.
 *
 * `popover="manual"` promotes the panel into the browser's TOP LAYER. There is
 * no `container-type` / `transform` / `filter` ancestor in this particular chain
 * today (verified by grep over `src/styles.scss` and the layout templates), so
 * plain `position: fixed` would work right now - but a top-layer element is
 * exempt from ancestor containment and overflow clipping by spec, so this cannot
 * be re-broken by an unrelated `container-type` landing on the nav later. That
 * trap produced invisible popovers on KAN-402 and is cheap to make impossible.
 * Feature-detected: environments without the Popover API (including jsdom) fall
 * back to the plain fixed positioning, which is still correct.
 *
 * Zoneless: `menuOpen`/`placement` are mutated from synchronous DOM handlers and
 * from an async stats subscription; `cdr.detectChanges()` after opening is a
 * deliberate synchronous force so the panel is in the DOM to be measured within
 * the same handler.
 */

import {
  DestroyRef,
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

import { IconComponent } from '../../shared/components/icon/icon.component';
import { CourseInvitationService } from '../../shared/services/course-invitation.service';
import { NotificationStateService } from '../../shared/services/notification-state.service';
import { InvitationStats } from '../../shared/types/invitation.types';
import {
  NotificationPanelPlacement,
  computeNotificationPanelPlacement,
} from './notification-panel-position';

let bellUid = 0;

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent],
  template: `
    <div class="notification-bell">
      <button
        #triggerEl
        type="button"
        class="btn btn-ghost btn-circle"
        aria-label="Notifications"
        [attr.aria-expanded]="menuOpen ? 'true' : 'false'"
        [attr.aria-controls]="panelId"
        data-testid="notification-bell-trigger"
        (click)="toggleMenu($event)">
        <div class="indicator">
          <app-icon name="notifications" size="sm"></app-icon>
          <span class="badge badge-sm badge-primary indicator-item"
                *ngIf="totalNotifications > 0">
            {{ totalNotifications > 99 ? '99+' : totalNotifications }}
          </span>
        </div>
      </button>

      @if (menuOpen) {
        <div
          #panelEl
          popover="manual"
          role="dialog"
          aria-label="Notifications"
          [id]="panelId"
          data-testid="notification-bell-panel"
          class="notification-bell-panel menu bg-base-100 rounded-box p-2 shadow-lg border border-base-300"
          [style.top.px]="placement.top"
          [style.left.px]="placement.left"
          [style.width.px]="placement.width"
          [style.max-height.px]="placement.maxHeight">
          <!-- Header -->
          <div class="px-4 py-3 border-b border-base-300">
            <div class="flex justify-between items-center gap-2">
              <h3 class="font-semibold min-w-0">Notifications</h3>
              <span class="badge badge-sm flex-shrink-0" *ngIf="totalNotifications > 0">
                {{ totalNotifications }}
              </span>
            </div>
          </div>

          <!-- Quick Stats -->
          <div class="p-4 space-y-2" *ngIf="stats">
            <div class="flex justify-between items-center gap-2" *ngIf="stats.pendingReceived > 0">
              <span class="text-sm min-w-0">Course Invitations</span>
              <span class="badge badge-primary badge-sm flex-shrink-0">{{ stats.pendingReceived }}</span>
            </div>

            <div class="flex justify-between items-center gap-2" *ngIf="stats.pendingSent > 0">
              <span class="text-sm min-w-0">Pending Invites Sent</span>
              <span class="badge badge-info badge-sm flex-shrink-0">{{ stats.pendingSent }}</span>
            </div>

            <div class="flex justify-between items-center gap-2" *ngIf="stats.recentAccepted > 0">
              <span class="text-sm min-w-0">Recent Acceptances</span>
              <span class="badge badge-success badge-sm flex-shrink-0">{{ stats.recentAccepted }}</span>
            </div>
          </div>

          <!-- Actions -->
          <div class="p-2 border-t border-base-300">
            <a routerLink="/invitations"
               class="btn btn-sm w-full"
               *ngIf="totalNotifications > 0"
               (click)="closeMenu()">
              View All Invitations
            </a>

            <div *ngIf="totalNotifications === 0"
                 class="text-center py-4 text-base-content opacity-50">
              <app-icon name="notifications_none" size="md" class="mb-2"></app-icon>
              <p class="text-sm">No new notifications</p>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .notification-bell {
      position: relative;
      display: inline-flex;
    }

    /*
     * KAN-592: viewport-relative placement. top/left/width/max-height are all
     * written from TS (see computeNotificationPanelPlacement) - there is
     * deliberately NO w-80 and no dropdown-end here, because both of those are
     * the defect. inset: unset and margin: 0 neutralise the [popover] UA
     * stylesheet's own inset: 0; margin: auto centring fallback, which would
     * otherwise fight the explicit coordinates.
     *
     * overflow-y: auto lets a long list scroll INSIDE the capped height. It is
     * deliberately not overflow-x: the repo treats overflow-x: hidden as a lie
     * that hides the very measurement this card is about, so horizontal
     * containment is done by the width cap and nothing clips it.
     */
    .notification-bell-panel {
      position: fixed;
      inset: unset;
      margin: 0;
      z-index: 1200;
      display: block;
      overflow-y: auto;
      overflow-x: visible;
    }

    .indicator-item {
      transform: translate(50%, -50%);
    }
  `]
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  @ViewChild('triggerEl') private triggerRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('panelEl') private panelRef?: ElementRef<HTMLDivElement>;

  readonly panelId = `notification-bell-panel-${bellUid++}`;

  stats: InvitationStats | null = null;
  totalNotifications = 0;

  menuOpen = false;
  /** Off-screen until the first real measurement, so nothing flashes at 0,0. */
  placement: NotificationPanelPlacement = { top: -9999, left: -9999, width: 0, maxHeight: 0 };

  private listenersAttached = false;

  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (!this.menuOpen) return;
    const target = event.target as Node | null;
    if (target && this.hostRef.nativeElement.contains(target)) return;
    // The panel lives in the top layer, so it is NOT a DOM descendant of the
    // host as far as containment is concerned in every engine - check it too.
    if (target && this.panelRef?.nativeElement.contains(target)) return;
    this.closeMenu();
  };

  private readonly onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.menuOpen) return;
    event.preventDefault();
    this.closeMenu();
    this.triggerRef?.nativeElement.focus();
  };

  /**
   * A resize changes the viewport AND re-runs the header's flex-wrap, which
   * moves the bell - the two inputs the placement is computed from. Recompute
   * rather than close, so rotating a phone does not dismiss the panel.
   */
  private readonly onWindowResize = (): void => {
    if (this.menuOpen) this.positionPanel();
  };

  /** A fixed panel would drift away from its trigger on scroll; close instead. */
  private readonly onAncestorScroll = (): void => {
    if (this.menuOpen) this.closeMenu();
  };

  private hostRef = inject(ElementRef<HTMLElement>);

  constructor(
    private invitationService: CourseInvitationService,
    private notificationStateService: NotificationStateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Subscribe to the shared notification count for immediate badge updates
    this.notificationStateService.notificationCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(count => {
        this.totalNotifications = count;
        // Force immediate UI update
        this.cdr.detectChanges();
      });

    // Load initial stats for the dropdown details
    this.loadStats();
  }

  ngOnDestroy(): void {
    this.detachGlobalListeners();
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (this.menuOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  closeMenu(): void {
    if (!this.menuOpen) return;
    // Explicit hide before @if removes the node. Removal auto-hides a shown
    // popover too, but hiding explicitly keeps the top layer deterministic.
    // Guarded on :popover-open - hidePopover() throws InvalidStateError when the
    // element is not currently shown (e.g. showPopover was never available).
    const panelEl = this.panelRef?.nativeElement;
    if (panelEl && typeof panelEl.hidePopover === 'function' && panelEl.matches(':popover-open')) {
      panelEl.hidePopover();
    }
    this.menuOpen = false;
    this.detachGlobalListeners();
    this.cdr.detectChanges();
  }

  private openMenu(): void {
    // KAN-670: the shell (and this bell) now stays mounted for the session, so
    // the breakdown is refreshed on open instead of riding on a rebuild per page.
    this.loadStats();
    this.menuOpen = true;
    // Force the panel into the DOM synchronously so it can be measured and
    // placed within this same handler - a deliberate synchronous force, not an
    // async-mutation-without-cdr bug.
    this.cdr.detectChanges();

    const panelEl = this.panelRef?.nativeElement;
    if (panelEl && typeof panelEl.showPopover === 'function') {
      panelEl.showPopover();
    }

    this.positionPanel();
    this.attachGlobalListeners();
    this.cdr.detectChanges();
  }

  private positionPanel(): void {
    const triggerEl = this.triggerRef?.nativeElement;
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    this.placement = computeNotificationPanelPlacement(
      { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    this.cdr.detectChanges();
  }

  private attachGlobalListeners(): void {
    if (this.listenersAttached) return;
    this.listenersAttached = true;
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onDocumentKeydown, true);
    // scroll does not bubble - capture phase catches every ancestor's scroll.
    document.addEventListener('scroll', this.onAncestorScroll, true);
    window.addEventListener('resize', this.onWindowResize);
  }

  private detachGlobalListeners(): void {
    if (!this.listenersAttached) return;
    this.listenersAttached = false;
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onDocumentKeydown, true);
    document.removeEventListener('scroll', this.onAncestorScroll, true);
    window.removeEventListener('resize', this.onWindowResize);
  }

  private loadStats(): void {
    // Stats for the dropdown details (not for the badge count): on creation and on every open.
    this.invitationService.getInvitationStats()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stats) => {
          this.stats = stats;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading notification stats:', error);
          this.stats = null;
          this.cdr.markForCheck();
        }
      });
  }
}
